import { FC, ReactNode } from 'react'
import { styled } from '@mui/material/styles'
import { MenuItem, Select as MuiSelect, FormControl, Box } from '@mui/material'
import { Icon } from '../../eid-icons'
import { Input } from './Input'
import { Skeleton } from '@mui/lab'
import { isNilOrEmpty } from 'packages/core'
import { useTranslation } from 'react-i18next'
import InfoTooltip from '../ApplicationFieldTypes/Components/InfoTooltip'
import { getInfoTooltipFieldsConfig, getInfoTooltipItems } from 'packages/core'


const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: '#919193',
    fontSize: '1.6rem',
    borderRadius: '0.4rem',
    padding: '0.5rem 1.2rem',
    wordBreak: 'break-word',

    '&:hover': {
        backgroundColor: '#f5f6f8 !important',
    },
}))

const StyledFormControl = styled(FormControl)({
    width: '100%',
    position: 'relative',
    margin: 0,
})

const StyledMuiSelect = styled(MuiSelect)({
    boxShadow: '0 0.4rem 0.6rem -0.2rem rgba(0, 0, 0, 0.1)',
    transition:
        'border 0.15s ease-in-out 0s, background 0.15s ease-in-out 0s, box-shadow 0.15s ease-in-out 0s',
    '& .MuiSelect-iconOpen': {
        transform: 'rotate(180deg) !important',
    },
    '& .MuiSelect-select': {
        minHeight: '1rem !important',
    },
})

const PlaceholderBox = styled(Box)({
    left: '1rem',
    color: '#b4b4b4',
    fontSize: '1.6rem',
    top: '50%',
    transform: 'translateY(-50%)',
    zIndex: 1,
    pointerEvents: 'none',
    width: 'fit-content !important',
    background: 'transparent !important',
})

const LabelBox = styled(Box)({
    color: '#73767A',
    fontSize: '1.2rem',
    paddingLeft: '0.8rem',
    marginBottom: '-0.4rem',
})

const DEFAULT_INFO_TOOLTIP_FIELDS = [
    { field: 'FriendlyName', labelKey: 'Common_FriendlyName' },
    { field: 'Description', labelKey: 'Common_Description' },
]

const SelectedOptionLabel = styled('span')({
    marginRight: '1rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
})

const menuProps: any = {
    PaperProps: {
        sx: {
            marginTop: '0.4rem',
            padding: '0 0.6rem',
            maxHeight: '20rem',
            border: '0.1rem solid #d4d4d8',
            boxShadow: '0 0.4rem 0.6rem 0 rgb(0 0 0 / 7%)',
            backgroundColor: '#ffffff',
            paddingLeft: '0.6rem',
            borderRadius: '0.4rem',
        },
    },
    sx: {
        '& .Mui-selected': {
            color: '#000000 !important',
            backgroundColor: '#f5f6f8 !important',
        },
    },
    anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
    },
    transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
    },
    getContentAnchorEl: null,
}

// @ts-ignore
export interface SelectProps extends React.ComponentProps<typeof MuiSelect> {
    value?: any
    options?: any
    isLoading?: boolean
    onChange?: any
    getOptionLabel?: any
    clearIcon?: any
    label?: any
    nullableText?: any
    children?: ReactNode
    onOpen?: () => void
    onClose?: () => void
    placeholder?: string
    disabled?: boolean
    showInfoTooltip?: boolean
    disableScrollLock?: boolean
    menuItemStyles?: React.CSSProperties
}

export const Select: FC<SelectProps> = (props) => {
    const {
        options,
        getOptionLabel,
        value,
        onChange,
        onOpen,
        onClose,
        label,
        placeholder,
        isLoading,
        disabled,
        nullableText,
        showInfoTooltip,
        disableScrollLock = false,
        menuItemStyles,
    } = props

    const { t } = useTranslation()
    const isSelected = (option: any) => option.id === value?.id
    const infoTooltipFieldsConfig = (() => {
        const configFields = getInfoTooltipFieldsConfig()
        return configFields.length > 0 ? configFields : DEFAULT_INFO_TOOLTIP_FIELDS
    })()

    return (
        <>
            {label && <LabelBox>{label}</LabelBox>}

            <StyledFormControl variant="standard" disabled={disabled}>
                {isNilOrEmpty(value) && (
                    <PlaceholderBox sx={{ position: 'absolute !important' }}>
                        {placeholder}
                    </PlaceholderBox>
                )}

                <StyledMuiSelect
                    variant="standard"
                    value={value}
                    renderValue={(value: any) => (
                        <SelectedOptionLabel style={menuItemStyles} title={getOptionLabel(value)}>
                            {getOptionLabel(value)}
                        </SelectedOptionLabel>
                    )}
                    input={<Input />}
                    MenuProps={{ ...menuProps, disableScrollLock }}
                    IconComponent={(props: any) => (
                        <Icon
                            name="Arrow"
                            className={`material-icons ${props.className}`}
                            style={{
                                right: '12px',
                                width: '20px',
                                top: 'auto',
                            }}
                            color="#919193"
                            {...props}
                        />
                    )}
                    onChange={(e: any) => {
                        onChange(e.target.value)
                    }}
                    onOpen={onOpen}
                    onClose={onClose}
                >
                    {!isLoading && nullableText && (
                        <StyledMenuItem
                            value={''}
                            key={`nullable-option-${nullableText}`}
                        >
                            {nullableText}
                        </StyledMenuItem>
                    )}
                    {!isLoading &&
                        options.map((o: any) => (
                            <StyledMenuItem
                                value={o}
                                key={o.id}
                                data-closeEvent="prevent"
                                selected={isSelected(o)}
                                style={menuItemStyles}
                            >
                                <Box sx={{ display: 'flex' }}>
                                    {getOptionLabel(o)} 
                                    {showInfoTooltip && (
                                        <InfoTooltip
                                            items={getInfoTooltipItems(o, infoTooltipFieldsConfig, t)}
                                            style={{ marginTop: '0.2rem' }}
                                        />
                                    )}
                                </Box>

                                {isSelected(o) && (
                                    <Icon
                                        name="Check"
                                        width="10px"
                                        height="7px"
                                        color="#7d7c7c"
                                        style={{ marginLeft: '1.2rem' }}
                                    />
                                )}
                            </StyledMenuItem>
                        ))}

                    {isLoading &&
                        [0, 1, 2].map((i) => (
                            <StyledMenuItem key={i}>
                                <Skeleton height={20} width="100%" />
                            </StyledMenuItem>
                        ))}
                </StyledMuiSelect>
            </StyledFormControl>
        </>
    )
}
